<template>
  <v-dialog
    :value="value"
    @input="$emit('input', $event)"
    max-width="100%"
    width="100%"
    height="100%"
    content-class="full-screen-image-dialog"
    persistent
    transition="dialog-bottom-transition"
    @click:outside="close"
  >
    <div v-if="item" class="full-screen-image-wrapper" @click.self="close">
      <v-btn icon dark class="full-screen-image-close" @click="close">
        <v-icon>mdi-close</v-icon>
      </v-btn>
      <div v-if="videoPlaybackError" class="full-screen-video-error">
        {{ videoPlaybackError }}
      </div>
      <div class="full-screen-image-container" ref="imageContainer">
        <img
          v-if="!videoPlaybackUrl"
          ref="imageRef"
          :src="imageUrl"
          :alt="item.kalan_pmiName"
          class="full-screen-image"
          @load="onImageLoad"
          @click.stop
        />
        <div v-if="videoPlaybackUrl" class="full-screen-video-wrapper">
          <video
            ref="videoPlayer"
            class="full-screen-video"
            autoplay
            loop
            @click="toggleVideoPause"
          ></video>
          <div class="full-screen-video-controls">
            <v-btn icon dark small @click="toggleVideoPause">
              <v-icon>{{ videoPaused ? 'mdi-play' : 'mdi-pause' }}</v-icon>
            </v-btn>
            <span class="full-screen-video-time">{{ videoTimeLabel }}</span>
            <input
              type="range"
              class="full-screen-video-progress"
              min="0"
              :max="videoDuration"
              step="0.1"
              :value="videoCurrentTime"
              @input="onVideoProgressInput"
            />
          </div>
        </div>
        <v-btn
          v-if="!videoPlaybackUrl"
          icon
          dark
          large
          class="full-screen-video-toggle full-screen-video-toggle--center"
          :loading="videoPlaybackLoading"
          :disabled="videoPlaybackLoading"
          @click.stop="toggleVideoPlayback"
        >
          <v-icon x-large>mdi-play-circle</v-icon>
        </v-btn>
        <v-btn
          v-if="videoPlaybackUrl"
          icon
          dark
          large
          class="full-screen-video-toggle full-screen-video-toggle--center"
          @click.stop="toggleVideoPlayback"
        >
          <v-icon x-large>mdi-stop</v-icon>
        </v-btn>
        <div
          v-if="!videoPlaybackUrl && item.rectangle && imageLayout"
          class="full-screen-rectangle-overlay"
          :style="overlayPositionStyle"
        >
          <div class="full-screen-rectangle-box" :style="getRectangleStyle(item.rectangle)"></div>
        </div>
      </div>
      <div class="full-screen-image-info">
        {{ formatDateTime(item.collectTime) }} — {{ item.kalan_pmiName }} -
        {{ item.kalan_cameraDev }}
      </div>
    </div>
  </v-dialog>
</template>

<script>
import moment from '@/util/dayjs-config';
import axios from 'axios';
import Hls from 'hls.js';

const PLAYBACK_API = '/api/intelli/video/playback';

export default {
  name: 'FullScreenAIResultViewer',
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    item: {
      type: Object,
      default: null,
    },
    getImageUrl: {
      type: Function,
      default(url) {
        if (!url) return '';
        const u = url.replace('/images/', '/api/intelli/images/');
        if (u.startsWith('http://') || u.startsWith('https://')) return u;
        return u;
      },
    },
  },
  data() {
    return {
      imageDimensions: {},
      imageLayout: null,
      videoPlaybackUrl: null,
      videoPlaybackError: null,
      videoPlaybackLoading: false,
      hlsInstance: null,
      videoPaused: false,
      videoCurrentTime: 0,
      videoDuration: 0,
    };
  },
  computed: {
    imageUrl() {
      return this.item ? this.getImageUrl(this.item.imageUrl) : '';
    },
    rectangleKey() {
      return this.item ? `fullscreen-${this.item.id}` : null;
    },
    overlayPositionStyle() {
      if (!this.imageLayout) return {};
      const
        {
          offsetX,
          offsetY,
          width,
          height
        } = this.imageLayout;
      return {
        left: `${offsetX}px`,
        top: `${offsetY}px`,
        width: `${width}px`,
        height: `${height}px`,
        right: 'auto',
        bottom: 'auto',
      };
    },
    videoTimeLabel() {
      if (!this.item || !this.item.collectTime) {
        return '00:00/00:00';
      }
      const startMoment = moment(this.item.collectTime).subtract(15, 'seconds');
      const displayMoment = startMoment.clone().add(Math.floor(this.videoCurrentTime), 'seconds');
      const currentStr = displayMoment.format('HH:mm:ss');
      return `${currentStr}`;
    },
  },
  watch: {
    value(isOpen) {
      if (isOpen) {
        document.body.style.overflow = 'hidden';
        document.addEventListener('keydown', this.onEscapeKey);
      } else {
        document.body.style.overflow = '';
        document.removeEventListener('keydown', this.onEscapeKey);
        this.imageLayout = null;
        if (this.rectangleKey) this.$delete(this.imageDimensions, this.rectangleKey);
        this.destroyVideoPlayback();
      }
    },
    item() {
      this.destroyVideoPlayback();
    },
  },
  beforeDestroy() {
    document.body.style.overflow = '';
    document.removeEventListener('keydown', this.onEscapeKey);
    this.destroyVideoPlayback();
  },
  methods: {
    normalizePlaybackUrl(apiUrl) {
      console.log('apiUrl', apiUrl);
      if (apiUrl == null || typeof apiUrl !== 'string') return '';
      const trimmed = apiUrl.trim();
      if (!trimmed) return '';
      try {
        const u = new URL(trimmed);
        const currentOrigin = `${window.location.protocol}//${window.location.host}`;
        const apiOrigin = `${currentOrigin}/api/intelli`;
        return `${apiOrigin}${u.pathname}${u.search}${u.hash}`;
      } catch (e) {
        console.log('error', e);
        return trimmed;
      }
    },
    async toggleVideoPlayback() {
      if (this.videoPlaybackUrl) {
        this.destroyVideoPlayback();
        return;
      }
      if (!this.item) return;
      const pmiId = this.item.kalan_pmiId ?? this.item.pmiId;
      const cameraDev = this.item.kalan_cameraDev ?? this.item.cameraDev;
      const date = this.item.collectTime
        ? moment(this.item.collectTime).toISOString()
        : null;
      if (pmiId == null || !cameraDev || !date) {
        this.videoPlaybackError = 'Datos insuficientes para reproducir el video.';
        return;
      }
      this.videoPlaybackError = null;
      this.videoPlaybackLoading = true;
      try {
        const response = await axios.post(PLAYBACK_API, {
          pmiId: Number(pmiId),
          cameraDev: String(cameraDev),
          date,
        }, { responseType: 'text' });
        const data = response.data;
        let url = null;
        const raw = typeof data === 'string' ? data.trim() : data;

        // const raw = 'http://localhost:3325/video/streams/21119f28-506b-43ef-afbb-583cb6aebeaf/playlist.m3u8'

        if (typeof raw === 'string' && (raw.startsWith('http://') || raw.startsWith('https://'))) {
          url = raw.replace(/^["']|["']$/g, '');
        } else if (typeof raw === 'string') {
          try {
            const parsed = JSON.parse(raw);
            if (parsed && (parsed.statusCode || parsed.error)) {
              this.videoPlaybackError = parsed.message || parsed.error || 'Error al obtener el video.';
              return;
            }
            if (parsed && parsed.url) url = parsed.url;
          } catch (_) {
            this.videoPlaybackError = 'Respuesta no válida del servidor.';
            return;
          }
        }
        if (!url) {
          this.videoPlaybackError = 'No se recibió la URL del video.';
          return;
        }
        this.videoPlaybackUrl = this.normalizePlaybackUrl(url);
        this.$nextTick(() => this.initHls());
      } catch (err) {
        const res = err.response;
        if (res && res.data) {
          const d = typeof res.data === 'string' ? (() => { try { return JSON.parse(res.data); } catch (_) { return {}; } })() : res.data;
          this.videoPlaybackError = d.message || d.error || res.statusText || 'Error al obtener el video.';
        } else {
          this.videoPlaybackError = err.message || 'Error al obtener el video.';
        }
      } finally {
        this.videoPlaybackLoading = false;
      }
    },
    initHls() {
      const video = this.$refs.videoPlayer;
      const url = this.videoPlaybackUrl;
      const urlString = url != null && typeof url === 'string' ? url.trim() : '';
      if (!video || !urlString) return;
      if (this.hlsInstance) {
        this.hlsInstance.destroy();
        this.hlsInstance = null;
      }
      const updateVideoState = () => {
        this.videoCurrentTime = video.currentTime || 0;
        this.videoDuration = (video.duration && !Number.isNaN(video.duration) && video.duration !== Infinity)
          ? video.duration
          : 0;
        this.videoPaused = video.paused;
      };
      video.addEventListener('timeupdate', updateVideoState);
      video.addEventListener('loadedmetadata', updateVideoState);
      video.addEventListener('durationchange', updateVideoState);
      video.addEventListener('play', () => { this.videoPaused = false; });
      video.addEventListener('pause', () => { this.videoPaused = true; });
      if (Hls.isSupported()) {
        const hls = new Hls();
        this.hlsInstance = hls;
        hls.loadSource(urlString);
        hls.attachMedia(video);
        hls.on(Hls.Events.ERROR, (_, data) => {
          if (data.fatal) this.videoPlaybackError = 'Error al cargar el video.';
        });
      } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
        video.src = urlString;
      }
    },
    toggleVideoPause() {
      const video = this.$refs.videoPlayer;
      if (!video) return;
      if (video.paused) {
        video.play();
      } else {
        video.pause();
      }
    },
    onVideoProgressInput(e) {
      const video = this.$refs.videoPlayer;
      if (!video) return;
      const val = parseFloat(e.target.value);
      video.currentTime = val;
      this.videoCurrentTime = val;
    },
    destroyVideoPlayback() {
      if (this.hlsInstance) {
        this.hlsInstance.destroy();
        this.hlsInstance = null;
      }
      this.videoPlaybackUrl = null;
      this.videoPlaybackError = null;
      this.videoPaused = false;
      this.videoCurrentTime = 0;
      this.videoDuration = 0;
      const video = this.$refs.videoPlayer;
      if (video) {
        video.src = '';
        video.load();
      }
    },
    onEscapeKey(event) {
      if (event.key === 'Escape') this.close();
    },
    close() {
      this.$emit('input', false);
    },
    formatDateTime(dateTimeString) {
      if (!dateTimeString) return '';
      return moment(dateTimeString).format('DD/MM/YYYY HH:mm:ss');
    },
    getRectangleStyle(rectangleString) {
      try {
        const rect = typeof rectangleString === 'string' ? JSON.parse(rectangleString) : rectangleString;
        if (!rect || typeof rect !== 'object') {
          return { display: 'none' };
        }

        const imgDims = this.imageDimensions[this.rectangleKey];

        if (
          imgDims
          && imgDims.naturalWidth
          && imgDims.naturalHeight
          && imgDims.displayWidth
          && imgDims.displayHeight
        ) {
          const containerAspect = imgDims.displayWidth / imgDims.displayHeight;
          const imageAspect = imgDims.naturalWidth / imgDims.naturalHeight;

          let scale = 1;
          let offsetX = 0;
          let offsetY = 0;

          if (imageAspect > containerAspect) {
            scale = imgDims.displayWidth / imgDims.naturalWidth;
            offsetY = (imgDims.displayHeight - imgDims.naturalHeight * scale) / 2;
          } else {
            scale = imgDims.displayHeight / imgDims.naturalHeight;
            offsetX = (imgDims.displayWidth - imgDims.naturalWidth * scale) / 2;
          }

          const rectLeft = (rect.x || 0) * imgDims.naturalWidth * scale + offsetX;
          const rectTop = (rect.y || 0) * imgDims.naturalHeight * scale + offsetY;
          const rectWidth = (rect.width || 0) * imgDims.naturalWidth * scale;
          const rectHeight = (rect.height || 0) * imgDims.naturalHeight * scale;

          const leftPercent = (rectLeft / imgDims.displayWidth) * 100;
          const topPercent = (rectTop / imgDims.displayHeight) * 100;
          const widthPercent = (rectWidth / imgDims.displayWidth) * 100;
          const heightPercent = (rectHeight / imgDims.displayHeight) * 100;

          return {
            position: 'absolute',
            left: `${Math.max(0, leftPercent)}%`,
            top: `${Math.max(0, topPercent)}%`,
            width: `${Math.min(100, widthPercent)}%`,
            height: `${Math.min(100, heightPercent)}%`,
            border: '2px solid #ff0000',
            boxSizing: 'border-box',
            pointerEvents: 'none',
            zIndex: 10,
          };
        }

        const leftPercent = (rect.x || 0) * 100;
        const topPercent = (rect.y || 0) * 100;
        const widthPercent = (rect.width || 0) * 100;
        const heightPercent = (rect.height || 0) * 100;

        return {
          position: 'absolute',
          left: `${leftPercent}%`,
          top: `${topPercent}%`,
          width: `${widthPercent}%`,
          height: `${heightPercent}%`,
          border: '2px solid #ff0000',
          boxSizing: 'border-box',
          pointerEvents: 'none',
          zIndex: 10,
        };
      } catch (error) {
        console.error('Error parsing rectangle:', error);
        return { display: 'none' };
      }
    },
    onImageLoad() {
      if (!this.item || !this.rectangleKey) return;
      const container = this.$refs.imageContainer;
      const img = this.$refs.imageRef;
      if (!container || !img || !img.naturalWidth) return;

      const applyLayout = () => {
        const displayWidth = img.offsetWidth || img.clientWidth;
        const displayHeight = img.offsetHeight || img.clientHeight;
        const offsetX = (container.clientWidth - displayWidth) / 2;
        const offsetY = (container.clientHeight - displayHeight) / 2;
        this.imageLayout = {
          offsetX,
          offsetY,
          width: displayWidth,
          height: displayHeight,
        };
        this.$set(this.imageDimensions, this.rectangleKey, {
          naturalWidth: img.naturalWidth,
          naturalHeight: img.naturalHeight,
          displayWidth,
          displayHeight,
        });
        this.$forceUpdate();
      };

      this.$nextTick(() => {
        this.$nextTick(applyLayout);
      });
    },
  },
};
</script>

<!-- Estilos globales (el dialog se teleporta fuera del componente) -->
<style lang="scss">
.full-screen-image-dialog {
  margin: 0 !important;
  max-height: 100vh !important;
  height: 100vh !important;
  background: rgba(0, 0, 0, 0.9) !important;
  box-shadow: none !important;
  overflow: hidden !important;

  .v-dialog__content {
    overflow: hidden !important;
    max-height: 100vh !important;
    height: 100vh !important;
  }

  .full-screen-image-wrapper {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100vh;
    max-height: 100vh;
    padding: 48px 16px 60px;
    box-sizing: border-box;
    overflow: hidden;
  }

  .full-screen-image-close {
    position: absolute !important;
    top: 16px;
    right: 16px;
    z-index: 10;
  }

  .full-screen-video-toggle {
    z-index: 10;
  }

  .full-screen-video-toggle--center {
    position: absolute !important;
    left: 50%;
    bottom: 5px;
    transform: translateX(-50%);
  }

  .full-screen-video-error {
    position: absolute;
    top: 56px;
    left: 50%;
    transform: translateX(-50%);
    padding: 8px 16px;
    background: rgba(200, 0, 0, 0.9);
    border-radius: 4px;
    color: #fff;
    font-size: 13px;
    max-width: 90%;
    text-align: center;
    z-index: 10;
  }

  .full-screen-image-container {
    position: relative;
    width: 100%;
    height: calc(100vh - 124px);
    max-height: calc(100vh - 124px);
    min-height: 0;
    overflow: hidden;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .full-screen-video-wrapper {
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
  }

  .full-screen-image-container .full-screen-image,
  .full-screen-image-container .full-screen-video {
    display: block;
    max-width: 100%;
    max-height: 100%;
    width: auto;
    height: auto;
    object-fit: contain;
  }

  .full-screen-video-controls {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 8px 12px;
    background: rgba(0, 0, 0, 0.7);
    border-radius: 4px;
    margin-top: 8px;
    min-width: 280px;
  }

  .full-screen-video-time {
    color: rgba(255, 255, 255, 0.9);
    font-size: 13px;
    font-variant-numeric: tabular-nums;
    min-width: 100px;
  }

  .full-screen-video-progress {
    flex: 1;
    min-width: 80px;
    height: 6px;
    -webkit-appearance: none;
    appearance: none;
    background: rgba(255, 255, 255, 0.3);
    border-radius: 3px;

    &::-webkit-slider-thumb {
      -webkit-appearance: none;
      appearance: none;
      width: 14px;
      height: 14px;
      border-radius: 50%;
      background: #fff;
      cursor: pointer;
    }

    &::-moz-range-thumb {
      width: 14px;
      height: 14px;
      border-radius: 50%;
      background: #fff;
      cursor: pointer;
      border: none;
    }
  }

  .full-screen-rectangle-overlay {
    position: absolute;
    pointer-events: none;
    z-index: 5;
  }

  .full-screen-rectangle-box {
    position: absolute;
    pointer-events: none;
    box-sizing: border-box;
  }

  .full-screen-image-info {
    position: absolute;
    bottom: 16px;
    left: 50%;
    transform: translateX(-50%);
    padding: 8px 16px;
    background: rgba(0, 0, 0, 0.6);
    border-radius: 4px;
    color: rgba(255, 255, 255, 0.9);
    font-size: 13px;
    max-width: 90%;
    text-align: center;
  }
}
</style>
